import cn from 'classnames'
import { Fragment } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Tooltip } from 'core-components/library/tooltip'
import { getDescriptionQualityReasonTranslation } from 'partner360/features/keyInitiatives/ui/helpers/getDescriptionQualityReasonTranslation'

import { TRANSLATION_KEYS as tr } from '../../../../translations'

import { DESCRIPTION_QUALITY_CONFIG } from './config'
import {
  getActionableDescriptionQualityReasons,
  getDescriptionQualityBand,
  isDescriptionQualityBlocking,
} from './helpers'
import { EDescriptionQualityBand, IDescriptionQualityResult } from './models'

import styles from './DescriptionQuality.module.scss'

interface IDescriptionQualityProps {
  quality?: IDescriptionQualityResult | null
  description?: string | null
  /** New forms show guidance until the existing blur/Apply validation is activated. */
  isValidationActive?: boolean
}

export const DescriptionQuality = ({
  quality,
  description,
  isValidationActive = true,
}: IDescriptionQualityProps) => {
  const showGuidance = !isValidationActive
  if (!quality && !showGuidance) {
    return null
  }

  const band = quality
    ? getDescriptionQualityBand(quality.score)
    : EDescriptionQualityBand.Insufficient
  const isBlocking = !showGuidance && isDescriptionQualityBlocking(quality)
  const visibleReasons = getActionableDescriptionQualityReasons(quality?.reasons ?? [])
  const hasReasons = visibleReasons.length > 0
  const isGreen =
    !isBlocking &&
    (band === EDescriptionQualityBand.Good || band === EDescriptionQualityBand.Strong)
  const showExample =
    showGuidance || !isGreen || (description !== undefined && !description?.trim())

  return (
    <div
      className={cn(styles.root, showGuidance ? styles.information : styles[band], {
        [styles.invalid]: isBlocking,
      })}
    >
      <div className={styles.content}>
        {showGuidance && (
          <Translation text={tr.keyInitiativeEditor.descriptionQuality.guidance} span />
        )}
        {!showGuidance && quality && (
          <>
            <div className={styles.score}>
              <Translation text={tr.keyInitiativeEditor.descriptionQuality.score} span />
              {DESCRIPTION_QUALITY_CONFIG.showScore && <strong>{quality.score}%</strong>}
            </div>

            {!hasReasons && !isBlocking && (
              <i className={cn('kbase', 'kbase-check', styles.okIcon)} aria-hidden="true" />
            )}

            {hasReasons && (
              <Tooltip
                inline
                className={styles.summaryTooltip}
                tipContent={() => (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipTitle}>
                      <Translation text={tr.keyInitiativeEditor.descriptionQuality.suggestions} />
                    </div>
                    <ul className={styles.reasonList}>
                      {visibleReasons.map((reason, index) => (
                        <li key={`${reason}-${index}`}>
                          <Translation text={getDescriptionQualityReasonTranslation(reason)} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              >
                <div className={styles.summary}>
                  {visibleReasons.map((reason, index) => (
                    <Fragment key={`${reason}-${index}`}>
                      {index > 0 && <span className={styles.separator}>; </span>}
                      <Translation
                        text={getDescriptionQualityReasonTranslation(reason, true)}
                        span
                      />
                    </Fragment>
                  ))}
                </div>
              </Tooltip>
            )}
          </>
        )}
      </div>
      {showExample && (
        <Tooltip
          inline
          className={styles.exampleTooltip}
          textAlign="left"
          tipContent={() => (
            <div className={styles.tooltip}>
              <Translation text={tr.keyInitiativeEditor.descriptionQuality.exampleTooltip} />
            </div>
          )}
        >
          <button type="button" className={styles.exampleLink}>
            <Translation text={tr.keyInitiativeEditor.descriptionQuality.example} span />
          </button>
        </Tooltip>
      )}
    </div>
  )
}

DescriptionQuality.displayName = 'DescriptionQuality'
